import type { LucideIcon } from 'lucide-react';
import { AlertTriangle, Check, Clock, FileSignature, Hourglass, KeyRound, PackagePlus, ReceiptText, RotateCcw, Smartphone, Undo2, Upload, UserCheck } from 'lucide-react';

export interface AcaoFluxoTriagem {
  key: string;
  titulo: string;
  legenda: string;
  Icone: LucideIcon;
  botaoClasse: string;
  legendaClasse: string;
  tipo: 'consolidada' | 'mover' | 'retomar';
  destinoColuna?: string;
  /** Quadro de destino. Omitido = mesmo quadro da triagem. */
  destinoQuadro?: string;
  /** Barra a ação enquanto o item não tiver número de cotação preenchido. */
  exigeCotacao?: boolean;
  motivo?: string;
}

export interface TriagemFluxoConfig {
  coluna: string;
  /** Quadro da coluna. Omitido = CONTRATO_PRIME. */
  quadro?: string;
  titulo: string;
  Icone: LucideIcon;
  iconeClasse: string;
  mostrarCobrar: boolean;
  /** false = sem trava de topo: qualquer pedido da fila pode ser trabalhado */
  fifo: boolean;
  /** true = botão 🕐 passar p/ fim da fila com justificativa+PRD (padrão Manter) */
  permitirManterFimFila?: boolean;
  /** true = o Manter é a justificativa da fila (motivo e ação da lista), a que a trava da BKO lê */
  justificativaDaFila?: boolean;
  /** true = mostra a coluna "Volta p/" com a fila de destino da retomada (Pedido Recuperado) */
  mostrarDestinoRetomada?: boolean;
  /** true = o pedido é de quem o criou (bko_id): o BKO vê só os seus e os sem dono (`bkoVeSoOsSeus`) */
  porDono?: boolean;
  /** Nome do Manter na legenda e no botão da linha. Omitido = "Fim da fila". */
  rotuloManter?: string;
  prazoHoras: number;
  acoes: AcaoFluxoTriagem[];
}

/* §5 da régua (17/08): ícone de ação da linha é SEMPRE muted — cor só no hover,
   e só nos dois de desfecho. Este é o que faz a fila ANDAR, então o hover é
   verde-garrafa. Antes era verde chapado o tempo todo, e com 13 linhas viravam
   13 manchas competindo com o bordô. */
const AVANCAR = 'h-7 w-7 rounded-[7px] border border-[#E4DDD3] bg-[#FAF8F5] text-muted-foreground transition-colors dark:border-border dark:bg-white/[0.04] [&_svg]:h-[14px] [&_svg]:w-[14px] hover:border-success/50 hover:!text-success';

/**
 * REFAZER CONTRATO (Douglas 05/08/2026) — a saída de volta para a criação.
 *
 * O pedido já tem contrato criado, mas o contrato precisa ser REFEITO: aparelho
 * em falta, e-mail errado, dado do contrato mudou. Sem esta ação o BKO só tinha
 * dois caminhos, os dois errados — ajustar o pedido na fila em que ele está
 * (ACEITE vira mesa de trabalho) ou cancelar um pedido vivo.
 *
 * É a MESMA ação que a Triagem Biometria já tem desde sempre
 * ([[TriagemBiometriaRow]] → `voltarParaCriacao`); as demais filas do Suporte
 * Comercial ficaram sem ela.
 *
 * ⚠️ NÃO substitui a retomada por snapshot (06/07): o recuperado continua
 * voltando para a etapa exata de onde foi cancelado. Isto é a saída CONSCIENTE
 * de lá — passa pela triagem, então entra no relatório do BKO, e o item carimba
 * `entrada_coluna_em` novo, caindo no FIM da fila FIFO de criação (regra 03/08).
 *
 * Fora daqui de propósito: PEDIDO FECHADO (contrato ainda não existe — já tem
 * "Iniciar Criação") e CRIAÇÃO PEDIDO (já é o destino).
 */
const REFAZER_CONTRATO: AcaoFluxoTriagem = {
  key: 'refazer-contrato',
  titulo: 'Refazer contrato — devolver para CRIAÇÃO PEDIDO (pedido inteiro)',
  legenda: 'Refazer contrato → Criação',
  Icone: Undo2,
  botaoClasse: 'h-7 w-7 rounded-[7px] border border-[#E4DDD3] bg-[#FAF8F5] text-muted-foreground transition-colors dark:border-border dark:bg-white/[0.04] [&_svg]:h-[14px] [&_svg]:w-[14px] hover:border-primary/50 hover:!text-primary',
  legendaClasse: 'text-amber-600',
  tipo: 'mover',
  destinoColuna: 'CRIAÇÃO PEDIDO',
};


/* ── PÓS-VENDA (Douglas 17/09/2026) ──────────────────────────────────────────
   "para tudo que hoje a Jéssica estava tentando fazer e não tínhamos uma saída
    boa, sempre com muitos clientes sem atendimento, pois estava uma bagunça."

   As 4 filas nasceram na migration 20260917020000. SEM TRIAGEM elas existem e
   NÃO ANDAM — o card fica na tela e a única forma de mover é arrastar, que é
   justamente o que a equipe não usa.

   O volume que essas filas passam a receber, medido em 30 dias antes de existirem:
     Minha Claro / Gestor de Dados   946 pedidos de ajuda
     eSIM / chip                     343
     contestação de fatura           148
   e o ciclo proativo afogado: 1.610 boas-vindas e 2.257 primeiras faturas.

   ⚠️ A REGRA DO MOVIMENTO: falar com o cliente NÃO move o card. A mensagem muda
   o ESTADO (o relógio do WhatsApp no card); só a ação concluída muda a FILA. É a
   mesma lei das transferências — "fechar não é atender". Se a conversa movesse
   sozinha, um "ok" do cliente faria o pedido parecer resolvido.

   ⚠️ Os prazos abaixo são PALPITE INICIAL, não régua do Douglas. Eles pintam o
   card de atrasado e alimentam a cobrança — a operação ajusta depois de ver a
   fila rodando.                                                             */

const POS_VENDA_BOAS_VINDAS: TriagemFluxoConfig = {
  coluna: 'BOAS-VINDAS',
  quadro: 'QUALIDADE',
  titulo: 'Triagem Boas-vindas',
  Icone: UserCheck,
  iconeClasse: 'text-primary',
  mostrarCobrar: true,
  fifo: false,
  permitirManterFimFila: true,
  prazoHoras: 48,
  // Boas-vindas é um ROTEADOR: aqui se descobre o que falta para o cliente usar
  // o que comprou, e manda para a fila certa. As três saídas já existem como
  // transição no banco (migration 20260917020000).
  acoes: [
    {
      key: 'bv-falta-chip',
      titulo: 'Ele ainda não ativou o chip ou o eSIM — mandar para ATIVAÇÃO DE CHIP',
      legenda: 'Falta o chip → Ativação de Chip',
      Icone: Smartphone,
      botaoClasse: AVANCAR,
      legendaClasse: 'text-green-600',
      tipo: 'mover',
      destinoColuna: 'ATIVAÇÃO DE CHIP',
      motivo: 'Triagem Boas-vindas — cliente ainda não ativou o chip/eSIM',
    },
    {
      key: 'bv-tudo-certo',
      titulo: 'Está usando tudo — segue para o acompanhamento da PRIMEIRA FATURA',
      legenda: 'Tudo certo → Primeira Fatura',
      Icone: Check,
      botaoClasse: AVANCAR,
      legendaClasse: 'text-green-600',
      tipo: 'mover',
      destinoColuna: 'PRIMEIRA FATURA',
      motivo: 'Triagem Boas-vindas — cliente usando, segue para a 1ª fatura',
    },
  ],
};

const POS_VENDA_CHIP: TriagemFluxoConfig = {
  coluna: 'ATIVAÇÃO DE CHIP',
  quadro: 'QUALIDADE',
  titulo: 'Triagem Validação eSIM',
  Icone: Smartphone,
  iconeClasse: 'text-primary',
  mostrarCobrar: true,
  fifo: false,
  permitirManterFimFila: true,
  prazoHoras: 24,
  // ⛔ SEM AÇÃO MANUAL PARA BOAS-VINDAS (Douglas 23/09/2026): "quando está
  // validado não tem que ter botão pra boas-vindas — isso deve ser automático".
  // A saída desta fila é a validação da última linha: o gatilho
  // fn_esim_concluir_familia_validada fecha a etapa e libera as boas-vindas.
  // Medido: 1.209 etapas fechadas em 7 dias, zero pelo botão. A tela desta
  // coluna é a TriagemEsim (TriagemFluxoModal), que não usa esta lista.
  acoes: [],
};

const POS_VENDA_PORTAL: TriagemFluxoConfig = {
  coluna: 'ACESSO AO PORTAL',
  quadro: 'QUALIDADE',
  titulo: 'Triagem Acesso ao Portal',
  Icone: KeyRound,
  iconeClasse: 'text-primary',
  mostrarCobrar: true,
  fifo: false,
  permitirManterFimFila: true,
  prazoHoras: 48,
  acoes: [
    {
      key: 'portal-ok',
      titulo: 'Ele já entra e enxerga as linhas — segue para PRIMEIRA FATURA',
      legenda: 'Acessa → Primeira Fatura',
      Icone: Check,
      botaoClasse: AVANCAR,
      legendaClasse: 'text-green-600',
      tipo: 'mover',
      destinoColuna: 'PRIMEIRA FATURA',
      motivo: 'Triagem Acesso ao Portal — cliente acessando o portal',
    },
    {
      key: 'portal-volta-bv',
      titulo: 'Não era isso que faltava — devolver para BOAS-VINDAS',
      legenda: 'Não era isso → Boas-vindas',
      Icone: Undo2,
      botaoClasse: AVANCAR,
      legendaClasse: 'text-amber-600',
      tipo: 'mover',
      destinoColuna: 'BOAS-VINDAS',
      motivo: 'Triagem Acesso ao Portal — devolvido para triagem de boas-vindas',
    },
  ],
};

const POS_VENDA_PRIMEIRA_FATURA: TriagemFluxoConfig = {
  coluna: 'PRIMEIRA FATURA',
  quadro: 'QUALIDADE',
  titulo: 'Triagem Primeira Fatura',
  Icone: ReceiptText,
  iconeClasse: 'text-primary',
  mostrarCobrar: true,
  fifo: false,
  permitirManterFimFila: true,
  prazoHoras: 72,
  acoes: [
    {
      key: 'fatura-conferida',
      titulo: 'PDF enviado e conferido com o cliente — concluir acompanhamento da primeira fatura',
      legenda: 'Conferida → Acompanhamento concluído',
      Icone: Check,
      botaoClasse: AVANCAR,
      legendaClasse: 'text-green-600',
      tipo: 'mover',
      destinoColuna: 'FINALIZADO PEDIDO (100%)',
      motivo: 'Triagem Primeira Fatura — conferida com o cliente',
    },
  ],
};

export const TRIAGENS_FLUXO: Record<string, TriagemFluxoConfig> = {
  'PEDIDO RECUPERADO': {
    coluna: 'PEDIDO RECUPERADO',
    titulo: 'Triagem Pedido Recuperado',
    Icone: RotateCcw,
    iconeClasse: 'text-amber-600',
    mostrarCobrar: true,
    fifo: true,
    permitirManterFimFila: true,
    mostrarDestinoRetomada: true,
    // (Douglas 27/10/2026) O recuperado conta da chegada aqui e entra no ciclo de 2 h do Contrato.
    prazoHoras: 2,
    acoes: [
      {
        key: 'retomar',
        titulo: 'Retomar Pedido — volta para a etapa de onde foi cancelado (pedido inteiro)',
        legenda: 'Retomar → etapa original',
        Icone: Check,
        botaoClasse: AVANCAR,
        legendaClasse: 'text-green-600',
        tipo: 'retomar',
      },
      // (Douglas 05/08/2026) REINPUTAR — a ordem morreu na operadora.
      //
      // A retomada devolve o item pro lugar EXATO do cancelamento (snapshot,
      // 06/07). Quando a operadora cancelou a ORDEM, esse lugar é mentira:
      // voltar pra INPUT REALIZADO FIXA/MOVEL diz que o input existe, e ele não
      // existe mais — tem que ser refeito.
      //
      // ⚠️ É ESCOLHA, não regra automática, e isso foi MEDIDO: dos 82 itens
      // recuperados a partir de INPUT REALIZADO, só 6 (7%) passaram pelo INPUT
      // depois. Automatizar por coluna erraria 93%; por produto não separa
      // (TT POS-PJ 29, FIBRA 15, TT FIXA 11, HA NOVO 8...); e por motivo também
      // não (DOC_PENDENTE: 46 recuperados, 2 reinputados). Exceção de 7% é
      // decisão de quem conhece o caso, não régua de sistema.
      //
      // INPUT roteia móvel/fixa sozinho por produto — um botão cobre fibra,
      // fibra next e TT.
      {
        key: 'reinputar',
        titulo: 'Reinputar — ordem cancelada na operadora, o input tem que ser refeito (vai para INPUT)',
        legenda: 'Reinputar → Input',
        Icone: Undo2,
        botaoClasse: 'h-7 w-7 rounded-[7px] border border-[#E4DDD3] bg-[#FAF8F5] text-muted-foreground transition-colors dark:border-border dark:bg-white/[0.04] [&_svg]:h-[14px] [&_svg]:w-[14px] hover:border-primary/50 hover:!text-primary',
        legendaClasse: 'text-indigo-600',
        tipo: 'mover',
        destinoColuna: 'INPUT',
      },
      REFAZER_CONTRATO,
    ],
  },
  'ACEITE': {
    coluna: 'ACEITE',
    titulo: 'Triagem Aceite',
    Icone: FileSignature,
    iconeClasse: 'text-purple-700',
    mostrarCobrar: true,
    fifo: false,
    permitirManterFimFila: true,
    prazoHoras: 5,
    acoes: [
      {
        key: 'input',
        titulo: 'Assinatura realizada — enviar para INPUT (pedido inteiro)',
        legenda: 'Assinatura OK → Input',
        Icone: Check,
        botaoClasse: AVANCAR,
        legendaClasse: 'text-green-600',
        tipo: 'consolidada',
      },
      REFAZER_CONTRATO,
    ],
  },
  'INPUT': {
    coluna: 'INPUT',
    porDono: true,
    titulo: 'Triagem Input',
    Icone: Upload,
    iconeClasse: 'text-indigo-700',
    mostrarCobrar: true,
    fifo: true,
    permitirManterFimFila: true,
    prazoHoras: 5,
    acoes: [
      {
        key: 'input-realizado',
        titulo: 'Pedido inputado — a móvel vai para Input realizado; a fixa pede a data e o turno da instalação e vai direto para Em instalação',
        legenda: 'Inputar',
        Icone: Check,
        botaoClasse: AVANCAR,
        legendaClasse: 'text-green-600',
        tipo: 'consolidada',
      },
      {
        key: 'pendencia',
        titulo: 'Pendência de input — mover para PENDENCIA INPUT',
        legenda: 'Pendência',
        Icone: Hourglass,
        botaoClasse: 'h-7 w-7 rounded-[7px] border border-[#E4DDD3] bg-[#FAF8F5] text-muted-foreground transition-colors dark:border-border dark:bg-white/[0.04] [&_svg]:h-[14px] [&_svg]:w-[14px] hover:border-primary/50 hover:!text-primary',
        legendaClasse: 'text-amber-600',
        tipo: 'mover',
        destinoColuna: 'PENDENCIA INPUT',
        motivo: 'Triagem Input — pendência de input',
      },
      {
        key: 'ahp',
        titulo: 'Aguardando HP — mover para AGUARDANDO HP',
        legenda: 'AHP',
        Icone: Clock,
        botaoClasse: 'h-7 w-7 rounded-[7px] border border-[#E4DDD3] bg-[#FAF8F5] text-muted-foreground transition-colors dark:border-border dark:bg-white/[0.04] [&_svg]:h-[14px] [&_svg]:w-[14px] hover:border-primary/50 hover:!text-primary',
        legendaClasse: 'text-blue-600',
        tipo: 'mover',
        destinoColuna: 'AGUARDANDO HP',
        motivo: 'Triagem Input — aguardando HP',
      },
      REFAZER_CONTRATO,
    ],
  },
  'PENDENCIA INPUT': {
    coluna: 'PENDENCIA INPUT',
    porDono: true,
    titulo: 'Triagem Pendência Input',
    Icone: Hourglass,
    iconeClasse: 'text-amber-600',
    mostrarCobrar: true,
    fifo: true,
    // (Douglas 05/08/2026) Cada pendência tem SLA e motivo próprios: a do topo
    // pode ser caso técnico sem solução enquanto a de baixo já está resolvida.
    // Sem esta saída o FIFO travava a fila inteira — 5 pedidos parados numa
    // média de 106h contra um prazo de 20h. Adiar exige justificativa e manda
    // para o FIM da fila, nunca para o topo.
    permitirManterFimFila: true,
    prazoHoras: 20,
    acoes: [
      {
        key: 'resolvida',
        titulo: 'Pendência resolvida — voltar para INPUT (pedido inteiro)',
        legenda: 'Resolvida → Input',
        Icone: Check,
        botaoClasse: AVANCAR,
        legendaClasse: 'text-green-600',
        tipo: 'mover',
        destinoColuna: 'INPUT',
        motivo: 'Triagem Pendência Input — pendência resolvida',
      },
      REFAZER_CONTRATO,
    ],
  },
  'AGUARDANDO HP': {
    coluna: 'AGUARDANDO HP',
    porDono: true,
    titulo: 'Triagem Aguardando HP',
    Icone: Clock,
    iconeClasse: 'text-sky-700',
    mostrarCobrar: true,
    fifo: true,
    // (Douglas 05/08/2026) Mesma razão da Pendência Input: HP complexo consome o
    // prazo inteiro enquanto o de baixo já liberou em horas.
    permitirManterFimFila: true,
    // (Douglas 27/10/2026) Entra no ciclo de 2 h do Contrato: sem tratar há 2 h,
    // trava primeiro. Justificar e manter dá mais 2 h; o HP que liberou é
    // inputado ali mesmo, sem voltar para a fila do Input.
    justificativaDaFila: true,
    rotuloManter: 'Justificar e manter',
    prazoHoras: 2,
    acoes: [
      {
        key: 'inputar',
        titulo: 'HP liberado — Inputar (o mesmo botão da Triagem Input: data e turno da instalação, a IA confere os prints do Conexão e do NETSALES, a fixa vai direto para Em instalação)',
        legenda: 'Inputar',
        Icone: Upload,
        botaoClasse: AVANCAR,
        legendaClasse: 'text-green-600',
        tipo: 'consolidada',
        destinoColuna: 'EM INSTALAÇÃO',
        destinoQuadro: 'TRAMITACAO_VIVO',
      },
      REFAZER_CONTRATO,
    ],
  },
  // 96% das saídas desta fila voltam para VALIDAÇÃO PENDENTE (883 de 921)
  'NEGOCIAÇÃO (ERRO DE INPUT)': {
    coluna: 'NEGOCIAÇÃO (ERRO DE INPUT)',
    quadro: 'TRAMITACAO_VIVO',
    titulo: 'Triagem Erro de Input',
    Icone: AlertTriangle,
    iconeClasse: 'text-amber-600',
    mostrarCobrar: true,
    fifo: false,
    permitirManterFimFila: true,
    justificativaDaFila: true,
    prazoHoras: 5,
    acoes: [
      {
        key: 'erro-resolvido',
        titulo: 'Erro resolvido — devolver para VALIDAÇÃO PENDENTE (exige cotação preenchida)',
        legenda: 'Resolvido → Validação Pendente',
        Icone: Check,
        botaoClasse: AVANCAR,
        legendaClasse: 'text-green-600',
        tipo: 'mover',
        destinoColuna: 'VALIDAÇÃO PENDENTE',
        exigeCotacao: true,
        motivo: 'Triagem Erro de Input — erro resolvido',
      },
      {
        key: 'voltar-erro-solar',
        titulo: 'Refazer input — devolver para Triagem Erro Solar (pedido inteiro; lá a cotação é trocada)',
        legenda: 'Voltar → Erro Solar',
        Icone: Undo2,
        botaoClasse: 'h-7 w-7 rounded-[7px] border border-[#E4DDD3] bg-[#FAF8F5] text-muted-foreground transition-colors dark:border-border dark:bg-white/[0.04] [&_svg]:h-[14px] [&_svg]:w-[14px] hover:border-primary/50 hover:!text-primary',
        legendaClasse: 'text-amber-600',
        tipo: 'mover',
        destinoColuna: 'ERRO SOLAR  INPUT MANUAL',
        motivo: 'Triagem Erro de Input — devolvido para Erro Solar',
      },
    ],
  },
  // 89% das saídas desta fila voltam para INSPEÇÃO CONCLUIDA (203 de 229)
  'PEDIDO COMPLEMENTAR': {
    coluna: 'PEDIDO COMPLEMENTAR',
    quadro: 'PRE_ATIVO',
    titulo: 'Triagem Pedido Complementar',
    Icone: PackagePlus,
    iconeClasse: 'text-sky-700',
    mostrarCobrar: true,
    fifo: false,
    permitirManterFimFila: true,
    justificativaDaFila: true,
    prazoHoras: 20,
    acoes: [
      {
        key: 'complemento-ok',
        titulo: 'Complemento concluído — devolver para INSPEÇÃO CONCLUIDA',
        legenda: 'Concluído → Inspeção Concluída',
        Icone: Check,
        botaoClasse: AVANCAR,
        legendaClasse: 'text-green-600',
        tipo: 'mover',
        destinoColuna: 'INSPEÇÃO CONCLUIDA',
        motivo: 'Triagem Pedido Complementar — complemento concluído',
      },
    ],
  },
  'BOAS-VINDAS': POS_VENDA_BOAS_VINDAS,
  'ATIVAÇÃO DE CHIP': POS_VENDA_CHIP,
  'ACESSO AO PORTAL': POS_VENDA_PORTAL,
  'PRIMEIRA FATURA': POS_VENDA_PRIMEIRA_FATURA,
};
